import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import AppLayout from '../components/AppLayout';
import './Invoices.css';

function formatAmount(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
}
function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  date.setHours(date.getHours() - 1);
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const LIMIT = 20;

export default function Invoices() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimeout = React.useRef(null);

  const fetchInvoices = useCallback(async (off = 0, st = status, q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: off, status: st });
      if (q.trim().length >= 2) params.set('search', q.trim());
      const res = await axios.get(`/api/invoices?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(res.data.invoices || []);
      setTotal(res.data.total || 0);
      setOffset(off);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [token, status, search]);

  useEffect(() => { fetchInvoices(0, status, search); }, [status, search]); // eslint-disable-line

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { setSearch(val); setOffset(0); }, 400);
  };
  const clearSearch = () => { setSearchInput(''); setSearch(''); setOffset(0); };

  const unpaidCount = invoices.filter(i => i.paye === 'non').length;

  return (
    <AppLayout>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <i className="fa-solid fa-arrow-left" />
        </button>
        <div>
          <h1 className="page-title">
            <i className="fa-solid fa-file-invoice-dollar" style={{ color: 'var(--primary)', marginRight: 10 }} />
            Mes Factures
          </h1>
          <p className="page-subtitle">{total} facture{total > 1 ? 's' : ''} au total</p>
        </div>
        {unpaidCount > 0 && (
          <span className="badge badge-warning">
            <i className="fa-solid fa-circle-exclamation" /> {unpaidCount} impayée{unpaidCount > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Filtres statut */}
      <div className="invoice-filters">
        {[
          { key: 'all',       label: 'Toutes',      icon: 'fa-list' },
          { key: 'unpaid',    label: 'Non payées',   icon: 'fa-hourglass-half' },
          { key: 'paid',      label: 'Payées',       icon: 'fa-circle-check' },
          { key: 'cancelled', label: 'Annulées',     icon: 'fa-ban' },
        ].map(f => (
          <button
            key={f.key}
            className={`filter-btn ${status === f.key ? 'active' : ''}`}
            onClick={() => { setStatus(f.key); setOffset(0); }}
          >
            <i className={`fa-solid ${f.icon}`} /> {f.label}
          </button>
        ))}
      </div>

      {/* Recherche */}
      <div className="history-search-bar" style={{ marginBottom: 16 }}>
        <i className="fa-solid fa-magnifying-glass history-search-icon" />
        <input
          className="history-search-input"
          type="text"
          placeholder="Rechercher par motif, entreprise, montant..."
          value={searchInput}
          onChange={handleSearchInput}
        />
        {searchInput && (
          <button className="history-search-clear" onClick={clearSearch}>
            <i className="fa-solid fa-xmark" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="history-loading">
          <i className="fa-solid fa-spinner spinner" style={{ fontSize: 28, color: 'var(--primary)' }} />
          <span>Chargement...</span>
        </div>
      ) : error ? (
        <div className="history-error"><i className="fa-solid fa-circle-exclamation" /><p>{error}</p></div>
      ) : invoices.length === 0 ? (
        <div className="empty-state">
          <i className="fa-solid fa-file-invoice" />
          <p>Aucune facture</p>
          <span>Les factures émises à votre encontre apparaîtront ici</span>
        </div>
      ) : (
        <>
          <div className="invoice-list">
            {invoices.map(inv => <InvoiceRow key={inv.id} inv={inv} side="client" />)}
          </div>
          <div className="history-pagination">
            <button className="btn-secondary pagination-btn" disabled={offset === 0} onClick={() => fetchInvoices(offset - LIMIT)}>
              <i className="fa-solid fa-chevron-left" /> Précédent
            </button>
            <span className="pagination-info">{Math.floor(offset / LIMIT) + 1} / {Math.max(1, Math.ceil(total / LIMIT))}</span>
            <button className="btn-secondary pagination-btn" disabled={offset + LIMIT >= total} onClick={() => fetchInvoices(offset + LIMIT)}>
              Suivant <i className="fa-solid fa-chevron-right" />
            </button>
          </div>
        </>
      )}
    </AppLayout>
  );
}

export function InvoiceRow({ inv, side }) {
  const isPaid      = inv.paye === 'oui';
  const isCancelled = inv.paye === 'annulee';
  const stateClass  = isPaid ? 'invoice-paid' : isCancelled ? 'invoice-cancelled' : 'invoice-unpaid';
  const iconClass   = isPaid ? 'status-paid'  : isCancelled ? 'status-cancelled'  : 'status-unpaid';
  const icon        = isPaid ? 'fa-circle-check' : isCancelled ? 'fa-ban' : 'fa-hourglass-half';
  const badgeClass  = isPaid ? 'badge-paid'  : isCancelled ? 'badge-cancelled'  : 'badge-unpaid';
  const badgeLabel  = isPaid ? 'Payée'       : isCancelled ? 'Annulée'          : 'Non payée';
  return (
    <div className={`invoice-row ${stateClass}`}>
      <div className={`invoice-status-icon ${iconClass}`}>
        <i className={`fa-solid ${icon}`} />
      </div>
      <div className="invoice-info">
        <div className="invoice-top-line">
          <span className={`invoice-parties ${isCancelled ? 'invoice-strikethrough' : ''}`}>
            <span className="invoice-company">{inv.entreprise}</span>
            <i className="fa-solid fa-arrow-right tx-arrow" />
            <span className="invoice-client">{inv.nom_prenom_client}</span>
          </span>
          <span className={`invoice-badge ${badgeClass}`}>
            {badgeLabel}
          </span>
        </div>
        <div className={`invoice-motif ${isCancelled ? 'invoice-strikethrough' : ''}`}>{inv.motif}</div>
        <div className="invoice-meta">
          <span className="invoice-employee">
            <i className="fa-solid fa-user-tie" /> {inv.nom_prenom_employe}
          </span>
          <span className="invoice-date">
            <i className="fa-regular fa-clock" /> {formatDate(inv.date)}
          </span>
          <span className="invoice-id">#{inv.id}</span>
        </div>
      </div>
      <div className={`invoice-amount ${isPaid ? 'amount-paid' : isCancelled ? 'amount-cancelled' : 'amount-unpaid'} ${isCancelled ? 'invoice-strikethrough' : ''}`}>
        {formatAmount(inv.montant)}
      </div>
    </div>
  );
}
